import type { MouseEvent, ReactElement } from 'react';

import Menu from '@/components/Menu/Menu';
import { MenuItemType } from '@/components/Menu/types';
import Modal from '@/components/Modal/Modal';
import AMP_KEYS from '@/configs/amp-track-keys';
import { useFeedbackTerminology } from '@/entities/workspace';
import {
  FEEDBACK_GALLERY_DEFAULT_TITLE,
  getDefaultFilterSet,
  getFeedbackGalleryTitle,
} from '@/features/(dashboard)/feedback-gallery/model/constants';
import {
  DASHBOARD_REDIRECT_ID_PARAM,
  DASHBOARD_REDIRECT_ROW_INDEX_PARAM,
} from '@/features/Dashboard/types/constants';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import useDashboard2Store from '@/stores/Dashboard2Store';
import { track } from '@/utils/track/track';
import { buildObjQuery } from '@/utils/util';

import AddChartFromSavedChartModal from '../Modals/AddChartFromSavedChartModal';

type Props = {
  currentCharts?: string[];
  rowIndex?: number;
  anchor: ReactElement;
  actions?: {
    onClickCreateNewTitle?: () => void;
    onClickCreateNewDescription?: () => void;
    onClickCreateNewFeedbackGallery?: () => void;
  };
  onAnchorClickAction?: (e: MouseEvent) => void;
};

const AddChartDashboardMenu = (props: Props) => {
  const dashboard = useDashboard2Store((state) => state.currDashboard);
  if (!dashboard) throw new Error('dashboard is not exist');

  const dashboardId = dashboard.alias;
  const { isEditor } = useUserRole();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const wr = useWorkspaceRouter();
  const setTextAreaState = useDashboard2Store((state) => state.setTextArea);
  const setPendingFeedbackGallery = useDashboard2Store(
    (state) => state.setPendingFeedbackGallery,
  );
  const setEditingState = useDashboard2Store((state) => state.setEditingState);
  const addChartFromSavedChartModalState = useModalState();

  const onClickAddFromSavedCharts = async () => {
    track(AMP_KEYS.DASHBOARD_FROM_SAVED_CHART_CLICK);
    addChartFromSavedChartModalState.open();
  };

  const onClickCreateNewChart = () => {
    track(AMP_KEYS.DASHBOARD_CREATE_NEW_CHART_CLICK);
    const query = buildObjQuery({
      [DASHBOARD_REDIRECT_ID_PARAM]: dashboardId,
      [DASHBOARD_REDIRECT_ROW_INDEX_PARAM]: props.rowIndex || null,
    });
    wr.push(`/charts/create${query}`);
  };

  const onClickCreateNewTitle = () => {
    track(AMP_KEYS.DASHBOARD_ADD_TITLE_CLICK);
    setTextAreaState({
      status: true,
      text: '<h1>Section Title</h1>',
    });
  };

  const onClickCreateNewDescription = () => {
    track(AMP_KEYS.DASHBOARD_ADD_DESC_CLICK);
    setTextAreaState({
      status: true,
      text: '<p>description</p>',
    });
  };

  const onClickCreateNewFeedbackGallery = () => {
    setPendingFeedbackGallery({
      dashboardId,
      rowIndex: props.rowIndex ?? dashboard.rows?.length ?? 0,
      feedbacksSetting: {
        filter_set: getDefaultFilterSet(),
        title: getFeedbackGalleryTitle(
          FEEDBACK_GALLERY_DEFAULT_TITLE,
          replaceFeedbackTerminology,
        ),
        sort_by: 'date',
        order_by: 'desc',
      },
    });
    setEditingState(true);
  };

  return (
    <>
      <Menu
        modalWidth={200}
        triggerNode={props.anchor}
        onAnchorClickAction={props.onAnchorClickAction}
        options={[
          {
            label: 'From saved charts',
            onClick: onClickAddFromSavedCharts,
            disabled: !isEditor,
          },
          {
            label: 'Create new chart',
            onClick: onClickCreateNewChart,
            disabled: !isEditor,
          },
          {
            label: 'divider',
            type: MenuItemType.DIVIDER,
          },
          // {
          //   label: 'Smart brief',
          //   onClick: onClickCreateNewChartAnalysis,
          //   disabled: !isEditor,
          // },
          {
            label: 'Title',
            onClick:
              props.actions?.onClickCreateNewTitle || onClickCreateNewTitle,
            disabled: !isEditor,
          },
          {
            label: 'Description',
            onClick:
              props.actions?.onClickCreateNewDescription ||
              onClickCreateNewDescription,
            disabled: !isEditor,
          },
          {
            label: 'divider',
            type: MenuItemType.DIVIDER,
          },
          {
            label: 'Mentions',
            onClick:
              props.actions?.onClickCreateNewFeedbackGallery ||
              onClickCreateNewFeedbackGallery,
            disabled: !isEditor,
          },
        ]}
      />
      <Modal
        status={addChartFromSavedChartModalState.status}
        closeModal={addChartFromSavedChartModalState.close}
      >
        <AddChartFromSavedChartModal
          dashboardId={dashboardId}
          currentCharts={props.currentCharts}
          rowIndex={props.rowIndex}
        />
      </Modal>
    </>
  );
};

export default AddChartDashboardMenu;
